import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import axios from "axios";
import { api_url } from "../../../config";

import FormHeader from "./FormHeader";
import LoadingSpinner from "./LoadingSpinner";
import ProductSelector from "./ProductSelector";
import PriceSelector from "./PriceSelector";
import FormField from "./FormField";
import FormButtons from "./FormButtons";
import StatusMessage from "./StatusMessage";

const PlanForm = ({ isOpen, toggleForm, stripeData, fetchPlans }) => {
  const { token, id } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedPrice, setSelectedPrice] = useState("");
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [showDiscount, setShowDiscount] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .max(155, "Name must not exceed 155 characters"),
    description: Yup.string().required("Description is required"),
    price: Yup.string().typeError("").required("Price is required"),
    billable: Yup.string()
      .oneOf(
        ["monthly", "yearly", "quarterly", "semi-annually"],
        "Invalid billing period"
      )
      .required("Billing period is required"),
    app: Yup.string()
      .oneOf(
        [
          "pharmtrix-retail",
          "pharmtrix-wholesale",
          "labtrix",
          "centrix",
          "billtrix",
        ],
        "Invalid app type"
      )
      .required("App type is required"),
    features: Yup.string().required("Features are required"),
    branches_limit: Yup.string().required("Kindly input number of branches"),
    users_limit: Yup.string().required("Kindly input number of users"),
    stripe_product: Yup.string().required("Stripe product is required"),
    stripe_price: Yup.string().required("Stripe price is required"),
    apply_promo: Yup.string()
      .oneOf(["true", "false"], "Invalid promo selection")
      .required("Promo application status is required"),
    discounted_amount: Yup.string()
      .test('discount-required', 'Discounted amount is required when applying promo', function(value) {
        return this.parent.apply_promo === 'true' ? value && value.length > 0 : true;
      })
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const applyPromo = watch("apply_promo");

  useEffect(() => {
    setShowDiscount(applyPromo === "true");
  }, [applyPromo]);

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setSelectedProduct(productId);
    setSelectedPrice("");
    setValue("stripe_product", productId);
    setValue("price", "");

    const selectedProductObj = stripeData?.find(
      (product) => product.product.id === productId
    );

    if (selectedProductObj) {
      const appType = selectedProductObj.product.name
        .split(" ")[0]
        .toLowerCase();
      setValue("app", appType);
    }
  };

  const handlePriceChange = (e) => {
    const priceId = e.target.value;
    setSelectedPrice(priceId);
    setValue("stripe_price", priceId);

    const selectedPriceObj = selectedProductObj?.product.prices.find(
      (price) => price.id === priceId
    );
    if (selectedPriceObj) {
      setValue("price", selectedPriceObj.unit_amount);
    }
  };

  const selectedProductObj = stripeData?.find(
    (product) => product.product.id === selectedProduct
  );

  const AddPlan = async (data) => {
    try {
      if (!token) {
        setError("Authentication token is missing. Please log in again.");
        return;
      }

      setLoading(true);
      const planData = {
        ...data,
        apply_promo: data.apply_promo === "true",
        discounted_amount: data.apply_promo === "true" ? data.discounted_amount : null,
      };

      // Extract just the token value without the prefix
      const cleanToken = token.split('|')[1];

      const response = await axios.post(
        `${api_url}/v1/admin/${id}/plans`,
        planData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${cleanToken}`,
          },
        }
      );

      console.log("Plan added successfully:", response.data);
      setStatus("Plan added successfully");
      fetchPlans();
      setTimeout(() => {
        setStatus("");
        toggleForm();
      }, 1500);
      reset();
    } catch (error) {
      console.error("Error adding plan:", error);
      if (error.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError(error?.response?.data?.error || "An error occurred");
      }
      setTimeout(() => {
        setError("");
      }, 3500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed top-0 right-0 w-full md:w-1/3 h-full bg-white shadow-lg transform transition-transform duration-300 ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="p-6 h-full overflow-auto scrollbar-thin scrollbar-webkit">
        <FormHeader toggleForm={toggleForm} />

        <form onSubmit={handleSubmit(AddPlan)} className="space-y-4">
          {loading && <LoadingSpinner />}
          
          <ProductSelector 
            stripeData={stripeData} 
            selectedProduct={selectedProduct} 
            handleProductChange={handleProductChange} 
            errors={errors} 
          />

          <FormField 
            label="Package Name" 
            name="name" 
            register={register} 
            errors={errors} 
          />

          <FormField 
            label="App Type" 
            name="app" 
            register={register} 
            errors={errors} 
            readOnly={true}
          />

          {selectedProduct && (
            <PriceSelector 
              selectedProductObj={selectedProductObj} 
              selectedPrice={selectedPrice} 
              handlePriceChange={handlePriceChange} 
              errors={errors} 
            />
          )}

          <FormField 
            label="Description" 
            name="description" 
            register={register} 
            errors={errors} 
            type="textarea" 
          />

          <FormField 
            label="Billing Period" 
            name="billable" 
            register={register} 
            errors={errors} 
            type="select" 
            options={[
              { value: "monthly", label: "Monthly" },
              { value: "yearly", label: "Annually" },
              { value: "semi-annually", label: "Semi-annually" },
              { value: "quarterly", label: "Quarterly" },
            ]} 
          />

          <FormField 
            label="Features" 
            name="features" 
            register={register} 
            errors={errors} 
            placeholder="Enter features separated by commas" 
          />

          <input type="hidden" {...register("stripe_product")} />
          <input type="hidden" {...register("stripe_price")} />

          <FormField 
            label="Apply Promo" 
            name="apply_promo" 
            register={register} 
            errors={errors} 
            type="select" 
            options={[
              { value: "false", label: "No" },
              { value: "true", label: "Yes" },
            ]} 
          />

          {showDiscount && (
            <FormField 
              label="Discounted Amount" 
              name="discounted_amount" 
              register={register} 
              errors={errors} 
              type="number" 
              placeholder="Enter discount amount" 
            />
          )}

          <FormField 
            label="No. of branches" 
            name="branches_limit" 
            register={register} 
            errors={errors} 
            type="number" 
          />

          <FormField 
            label="No. of Users" 
            name="users_limit" 
            register={register} 
            errors={errors} 
            type="number" 
          />

          <FormButtons toggleForm={toggleForm} loading={loading} />
          <StatusMessage status={status} error={error} />
        </form>
      </div>
    </div>
  );
};

export default PlanForm; 